// framework/client/components/ForgotPassword.js
// (Replace the entire file content)

import { ComponentBase } from "../core/ComponentBase.js";
import { z } from "zod";

export class ForgotPassword extends ComponentBase {
    constructor(config) {
        super(config);
        this.container = document.createElement("div");
        this.container.className = "forgot-password-wizard";
        
        // --- NEW: Wizard State ---
        this.wizardState = {
            currentStep: 'request', // 'request' or 'reset'
            email: '',
            otpRequestId: null,
        };

        this.formRenderer = null;
        this._boundHandleSubmit = this.handleSubmit.bind(this);
    }

    async initialize() {
        this.sessionTokenHandler = this.appClient.sessionTokenHandler;
        await super.initialize();
    }

    async render() {
        this.container.innerHTML = ''; // Clear previous content
        
        if (this.wizardState.currentStep === 'request') {
            await this.renderRequestStep();
        } else if (this.wizardState.currentStep === 'reset') {
            await this.renderResetStep();
        }
        
        return this.container;
    }

    async renderRequestStep() {
        if (this.formRenderer) this.formRenderer.destroy();

        const headerContainer = document.createElement('div');
        headerContainer.className = 'text-center mb-2';
        headerContainer.innerHTML = `
            <h3 class="fw-bold mb-1 fs-4">${this.t("Forgot Password")}</h3>
            <p class="text-muted small mb-0">${this.t("Enter your email to receive a password reset code.")}</p>
        `;
        this.container.appendChild(headerContainer);

        const formConfig = {
            labelMode: 'floating',
            gapClass: 'mb-1',
            submitButtonLabel: "Send Reset Code",
            fields: [
                { name: "email", type: "email", label: "Email Address", required: true, autocomplete: "email" }
            ],
            validation: { validateOnChange: true }
        };

        const requestSchema = z.object({
            email: z.string().email({ message: "Please enter a valid email address" }),
        });

        this.formRenderer = await this.createManagedComponent('GenericFormRenderer', null, {
            instanceId: `${this.config.instanceId}-request-form`,
            formId: 'forgotPasswordRequestForm',
            formConfig: formConfig,
            schema: requestSchema,
            compact: true
        });

        const formElement = await this.formRenderer.render();
        this.formRenderer.on('formSubmit', this._boundHandleSubmit);
        this.container.appendChild(formElement);
    }

    async renderResetStep() {
        if (this.formRenderer) this.formRenderer.destroy();

        const headerContainer = document.createElement('div');
        headerContainer.className = 'text-center mb-2';
        headerContainer.innerHTML = `
            <h3 class="fw-bold mb-1 fs-4">${this.t("Reset Password")}</h3>
            <p class="text-muted small mb-0">${this.t("A reset code has been sent to")} <strong class="text-body">${this.wizardState.email}</strong>.</p>
        `;
        this.container.appendChild(headerContainer);

        const formConfig = {
            labelMode: 'floating',
            gapClass: 'mb-1',
            submitButtonLabel: "Reset Password & Log In",
            fields: [
                { name: "otpCode", type: "otp", label: "6-Digit Code", required: true, autocomplete: "one-time-code" },
                { name: "newPassword", type: "password", label: "New Password", required: true, autocomplete: "new-password" }
            ],
            validation: { validateOnChange: true }
        };

        const resetSchema = z.object({
            otpCode: z.string().length(6, "Code must be 6 digits."),
            newPassword: z.string().min(8, "Password must be at least 8 characters."),
        });

        this.formRenderer = await this.createManagedComponent('GenericFormRenderer', null, {
            instanceId: `${this.config.instanceId}-reset-form`,
            formId: 'forgotPasswordResetForm',
            formConfig: formConfig,
            schema: resetSchema,
            compact: true
        });

        const formElement = await this.formRenderer.render();
        this.formRenderer.on('formSubmit', this._boundHandleSubmit);
        this.container.appendChild(formElement);
    }

    async handleSubmit() {
        if (await this.formRenderer.isFormValid()) {
            if (this.wizardState.currentStep === 'request') {
                await this.handleRequestSubmit();
            } else if (this.wizardState.currentStep === 'reset') {
                await this.handleResetSubmit();
            }
        } else {
            this.log.warn("Submit blocked due to form validation errors.");
        }
    }

    async handleRequestSubmit() {
        const { email } = this.formRenderer.getFormData();
        try {
            // The forgotPassword RPC now initiates the OTP flow
            const result = await this.sessionTokenHandler.forgotPassword(email);
            
            this.wizardState.email = email;
            this.wizardState.otpRequestId = result.otpRequestId;
            this.wizardState.currentStep = 'reset';
            
            this.appClient.notifier.success("A verification code has been sent to your email.");
            this.render(); // Re-render to show the reset step

        } catch (apiError) {
            this.formRenderer.setSubmitting(false);
            this.appClient.notifier.error(apiError.message, { title: "Error" });
        }
    }

    async handleResetSubmit() {
        const { otpCode, newPassword } = this.formRenderer.getFormData();
        try {
            // The resetPassword RPC now takes the OTP details
            const result = await this.sessionTokenHandler.resetPassword({
                otpRequestId: this.wizardState.otpRequestId,
                otpCode: otpCode,
                newPassword: newPassword
            });

            this.appClient.notifier.success("Password has been reset successfully! Please log in with your new password.");
            
            // Redirect to login page upon success
            this.appClient.layoutEngine.navigate('/entry/login');

        } catch (apiError) {
            this.formRenderer.setSubmitting(false);
            this.appClient.notifier.error(apiError.message, { title: "Password Reset Failed" });
        }
    }

    destroy() {
        if (this.formRenderer) {
            this.formRenderer.off('formSubmit', this._boundHandleSubmit);
        }
        super.destroy();
    }
}